<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0">Inventaires ({{ totalRows }})</h1>
        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['inventories', 'Inventory', 'InventoryItem']" label="Inventaires" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>
          <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="openCreateDialog">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouvel inventaire</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- Filtres -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <form class="row g-2 align-items-end" @submit.prevent="applyFilters">
            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Statut</label>
              <select v-model="filters.status" class="form-select form-select-sm">
                <option value="">Tous</option>
                <option value="draft">Brouillon</option>
                <option value="closed">Clos</option>
                <option value="validated">Validé</option>
              </select>
            </div>

            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Type</label>
              <select v-model="filters.type" class="form-select form-select-sm">
                <option value="">Tous</option>
                <option value="global">Global</option>
                <option value="partial">Partiel</option>
              </select>
            </div>

            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Date du</label>
              <input type="date" class="form-control form-control-sm" v-model="filters.date_from" />
            </div>

            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Date au</label>
              <input type="date" class="form-control form-control-sm" v-model="filters.date_to" />
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters"
                :disabled="isLoading">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="submit" class="btn btn-primary btn-sm btn-icon-label" :disabled="isLoading">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <BootstrapDataTable
        :response="inventoriesResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkDeleteInventories"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectInventoryForBulkDelete"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="inventories.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher référence ou description..."
        detail-route-name="InventoryShow"
        empty-text="Aucun inventaire trouvé."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchInventories"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="bulkActionPending || isOverLimit"
            @click="bulkDeleteInventories"
          >
            <i class="fa fa-trash me-1" aria-hidden="true"></i>Supprimer {{ selectedCount }}
          </button>
        </template>

        <template #cell-type="{ row }">
          <span class="badge" :class="row.type === 'global' ? 'bg-primary' : 'bg-info text-dark'">
            {{ row.type === 'global' ? 'Global' : 'Partiel' }}
          </span>
        </template>

        <template #cell-status="{ row }">
          <span class="badge" :class="statusClass(row.status)">
            {{ statusLabel(row.status) }}
          </span>
        </template>

        <template #cell-scope="{ row }">
          <span v-if="row.type === 'global'" class="small text-muted">Tous les rayons</span>
          <span v-else-if="scopeRayons(row).length" class="scope-rayons">
            <span
              v-for="rayon in scopeRayons(row)"
              :key="rayon.id || rayon.name"
              class="scope-rayon-chip"
              :style="{ borderColor: rayonColor(rayon.color) }"
            >
              <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(rayon.color) }"></span>
              <span>{{ rayon.name }}</span>
            </span>
          </span>
          <span v-else class="small text-muted">Rayons sélectionnés</span>
        </template>

        <template #cell-total_items="{ row }">
          {{ row.counted_items_count || 0 }} / {{ row.items_count || row.total_items || 0 }}
        </template>

        <template #cell-total_difference="{ row }">
          <span :class="[
            'fw-semibold',
            row.total_difference > 0
              ? 'text-success'
              : row.total_difference < 0
                ? 'text-danger'
                : ''
          ]">
            {{ row.total_difference }}
          </span>
        </template>

        <template #cell-created_at="{ row }">
          {{ $format.date(row.created_at) }}
        </template>

        <template #cell-validated_at="{ row }">
          {{ row.validated_at ? $format.date(row.validated_at) : '-' }}
        </template>

      </BootstrapDataTable>

      <div v-if="createDialogVisible" class="inventory-modal-backdrop" @click.self="closeCreateDialog">
        <form class="inventory-modal" @submit.prevent="createInventory">
          <div class="inventory-modal-header">
            <div>
              <h2 class="h5 mb-1">Nouvel inventaire brouillon</h2>
              <small class="text-muted">Le brouillon sera prérempli avec les produits actifs du périmètre choisi.</small>
            </div>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeCreateDialog">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <div class="inventory-modal-body">
            <div>
              <label class="form-label small fw-semibold">Type</label>
              <select v-model="createForm.type" class="form-select form-select-sm">
                <option value="global">Global</option>
                <option value="partial">Partiel par rayon</option>
              </select>
            </div>

            <div v-if="createForm.type === 'partial'">
              <label class="form-label small fw-semibold">Rayons</label>
              <select v-model="createForm.rayon_ids" class="form-select form-select-sm" multiple size="6">
                <option v-for="rayon in rayons" :key="rayon.id" :value="rayon.id">
                  {{ rayon.name }}
                </option>
              </select>
              <small class="text-muted">Maintenez Ctrl ou Cmd pour sélectionner plusieurs rayons.</small>
            </div>

            <div>
              <label class="form-label small fw-semibold">Description</label>
              <textarea
                v-model="createForm.description"
                class="form-control form-control-sm"
                rows="3"
                placeholder="Note interne, zone physique, consigne..."
              ></textarea>
            </div>
          </div>

          <div class="inventory-modal-footer">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeCreateDialog">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Annuler
            </button>
            <button type="submit" class="btn btn-primary btn-sm" :disabled="isCreatingInventory">
              <i class="fa" :class="isCreatingInventory ? 'fa-spinner fa-spin' : 'fa-check'"></i>
              Créer le brouillon
            </button>
          </div>
        </form>
      </div>
    </template>
  </AdminLayout>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import {
  cleanFilterParams,
  countActiveFilters,
  extractApiTotal,
  extractApiList,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';
import { bulkActionViewMixin } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';
import { normalizeRayonColor } from '@/utils/rayonColor';

const FILTER_MODULE = 'inventories';
const DEFAULT_FILTERS = {
  search: '',
  status: '',
  type: '',
  date_from: '',
  date_to: '',
};

export default {
  name: 'InventoriesView',
  mixins: [bulkActionViewMixin],
  setup() {
    const store = useStore();
    const currentUser = computed(() => store.getters['auth/currentUser']);
    const role = computed(() => (currentUser.value && currentUser.value.role) || '');
    const isGerant = computed(() => role.value === 'gerant');

    return {
      currentUser,
      role,
      isGerant,
    };
  },
  data() {
    return {
      inventories: [],
      inventoriesResponse: null,
      isLoading: false,
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'created_at',
        sortDir: 'desc',
      },
      tableColumns: [
        { key: 'reference', label: 'Référence' },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'status', label: 'Statut', class: 'text-center' },
        { key: 'scope', label: 'Rayons' },
        { key: 'total_items', label: 'Progression', sortable: true, class: 'text-end' },
        { key: 'total_difference', label: 'Écart total', sortable: true, class: 'text-end' },
        { key: 'created_at', label: 'Créé le', sortable: true },
        { key: 'validated_at', label: 'Validé le', sortable: true },
      ],
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,
      rayons: [],
      createDialogVisible: false,
      isCreatingInventory: false,
      createForm: {
        type: 'global',
        description: '',
        rayon_ids: [],
      },
      errorMessage: '',
      successMessage: '',
    };
  },
  mounted() {
    this.restoreFilters();
    this.fetchInventories();
    this.fetchRayons();
  },
  computed: {
    canBulkDeleteInventories() {
      return hasPermission(this.currentUser, 'inventories.delete');
    },
    totalRows() {
      return extractApiTotal(this.inventoriesResponse, this.inventories.length);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    canSelectInventoryForBulkDelete(inventory) {
      return inventory?.status === 'draft';
    },

    async bulkDeleteInventories() {
      await this.performBulkAction({
        url: '/inventories/bulk',
        method: 'delete',
        itemLabel: 'inventaire',
        confirmation: (count) => ({
          header: 'Supprimer les inventaires',
          message: `Supprimer les ${count} inventaires brouillon sélectionnés ?`,
          acceptLabel: `Supprimer ${count}`,
          acceptClass: 'p-button-danger',
        }),
        refresh: () => this.fetchInventories(),
      });
    },

    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    async fetchRayons() {
      try {
        const response = await this.$api.get('/rayons', {
          params: {
            only_active: 1,
            sort_by: 'name',
          },
        });
        this.rayons = response.data?.data || [];
      } catch (error) {
        console.error('Erreur chargement rayons', error);
      }
    },

    async fetchInventories() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const params = {
          ...cleanFilterParams(this.filters),
          paginate: 1,
          page: this.table.page,
          per_page: this.table.perPage,
          sort_by: this.table.sortBy,
          sort_dir: this.table.sortDir,
        };
        const response = await this.$api.get('/inventories', { params });
        this.inventoriesResponse = response.data;
        this.inventories = extractApiList(response.data);
      } catch (error) {
        console.error('Erreur chargement inventaires', error);
        this.errorMessage = 'Impossible de charger les inventaires.';
      } finally {
        this.isLoading = false;
      }
    },

    applyFilters() {
      this.table.page = 1;
      this.fetchInventories();
    },

    handleTableSearch(search) {
      this.filters.search = search;
      this.table.page = 1;
      this.fetchInventories();
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchInventories();
    },

    openCreateDialog() {
      this.createForm = {
        type: 'global',
        description: '',
        rayon_ids: [],
      };
      this.errorMessage = '';
      this.successMessage = '';
      this.createDialogVisible = true;
      if (!this.rayons.length) {
        this.fetchRayons();
      }
    },

    closeCreateDialog() {
      if (this.isCreatingInventory) return;
      this.createDialogVisible = false;
    },

    async createInventory() {
      this.errorMessage = '';
      this.successMessage = '';

      if (this.createForm.type === 'partial' && !this.createForm.rayon_ids.length) {
        this.errorMessage = 'Sélectionnez au moins un rayon pour un inventaire partiel.';
        return;
      }

      this.isCreatingInventory = true;

      try {
        const payload = {
          type: this.createForm.type,
          description: this.createForm.description || null,
        };

        if (this.createForm.type === 'partial') {
          payload.rayon_ids = this.createForm.rayon_ids.map((id) => Number(id));
        }

        await this.$api.post('/inventories', payload);
        this.successMessage = 'Inventaire brouillon créé.';
        this.createDialogVisible = false;
        this.table.page = 1;
        await this.fetchInventories();
      } catch (error) {
        this.errorMessage = this.errorMessageFrom(error, 'Impossible de créer cet inventaire.');
      } finally {
        this.isCreatingInventory = false;
      }
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchInventories();
    },

    handlePageChange(page) {
      this.table.page = page;
      this.fetchInventories();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchInventories();
    },

    statusLabel(status) {
      switch (status) {
        case 'draft':
          return 'Brouillon';
        case 'closed':
          return 'Clos';
        case 'validated':
          return 'Validé';
        default:
          return status;
      }
    },

    statusClass(status) {
      switch (status) {
        case 'draft':
          return 'bg-secondary';
        case 'closed':
          return 'bg-warning text-dark';
        case 'validated':
          return 'bg-success';
        default:
          return 'bg-secondary';
      }
    },

    scopeLabel(row) {
      if (row.type === 'global') {
        return 'Tous les rayons';
      }

      const rayons = row.scope?.rayons || [];
      if (!rayons.length) {
        return 'Rayons sélectionnés';
      }

      return rayons.map((rayon) => rayon.name).join(', ');
    },

    scopeRayons(row) {
      return row.scope?.rayons || [];
    },

    rayonColor(color) {
      return normalizeRayonColor(color);
    },

    errorMessageFrom(error, fallback) {
      return error.response && error.response.data && error.response.data.errors
        ? Object.values(error.response.data.errors).flat().join(' ')
        : fallback;
    },
  },
};
</script>

<style scoped>
.inventory-modal-backdrop {
  align-items: center;
  background: rgba(15, 23, 42, 0.42);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 1rem;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1080;
}

.inventory-modal {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1.25rem 3rem rgba(15, 23, 42, 0.22);
  max-width: 560px;
  overflow: hidden;
  width: min(560px, 100%);
}

.inventory-modal-header,
.inventory-modal-footer {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem;
}

.inventory-modal-header {
  border-bottom: 1px solid #e2e8f0;
}

.inventory-modal-footer {
  border-top: 1px solid #e2e8f0;
}

.inventory-modal-body {
  display: grid;
  gap: 1rem;
  padding: 1rem;
}

.scope-rayons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.scope-rayon-chip {
  align-items: center;
  background: #ffffff;
  border: 1px solid;
  border-radius: 999px;
  display: inline-flex;
  gap: 0.35rem;
  padding: 0.12rem 0.5rem;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  display: inline-block;
  height: 0.72rem;
  width: 0.72rem;
}

@media (max-width: 575.98px) {
  .inventory-modal-footer {
    align-items: stretch;
    flex-direction: column-reverse;
  }
}
</style>
